import React, { useState, useEffect, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../config';
import io from 'socket.io-client';
import '../styles/BattleshipMonopoly.css';

const TEAM_INFO = {
  red: { name: 'Tokyo', color: '#ff4444', piece: '🌸' },
  blue: { name: 'Vancouver', color: '#4488ff', piece: '🏔️' },
  green: { name: 'New York', color: '#44cc44', piece: '🗽' },
  yellow: { name: 'London', color: '#ffcc00', piece: '🕰️' },
};

const TEAM_ORDER = ['red', 'blue', 'green', 'yellow'];

const getAdminToken = () => localStorage.getItem('adminKey') || '';

const BattleshipMonopolyBoard = ({ isAdmin = false, compact = false }) => {
  const [board, setBoard] = useState(null);
  const [battleshipState, setBattleshipState] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [voteDuration, setVoteDuration] = useState(10);
  const [maxShooters, setMaxShooters] = useState(4);
  const [loading, setLoading] = useState(false);
  const socketRef = useRef(null);
  const timerRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const headers = isAdmin ? { 'x-admin-token': getAdminToken() } : {};
      const endpoint = isAdmin ? '/api/admin/monopoly/state' : '/api/monopoly/state';
      const res = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
      const data = await res.json();
      if (data.board) {
        setBoard(data.board);
      } else if (isAdmin) {
        const createRes = await fetch(`${API_BASE_URL}/api/admin/monopoly/create`, {
          method: 'POST',
          headers,
        });
        const createData = await createRes.json();
        if (createData.board) setBoard(createData.board);
      }
    } catch (err) {
      console.error('Failed to fetch monopoly state:', err);
    }
  }, [isAdmin]);

  const fetchBattleshipState = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/battleship/spectate`);
      const data = await res.json();
      if (data.game) setBattleshipState(data.game);
    } catch (err) {
      console.error('Failed to fetch battleship state:', err);
    }
  }, []);

  useEffect(() => {
    fetchState();
    fetchBattleshipState();

    const socket = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current = socket;

    socket.on('monopoly:state', (data) => setBoard(data));
    socket.on('monopoly:advance', (data) => setBoard(data.board));
    socket.on('monopoly:voting-start', (data) => {
      fetchState();
      if (data.endsAt) {
        startCountdown(data.endsAt);
      }
    });
    socket.on('monopoly:vote-count', () => fetchState());
    socket.on('monopoly:voting-end', () => {
      setTimeLeft(null);
      fetchState();
    });
    socket.on('monopoly:reveal', (data) => setBoard(data.board));
    socket.on('monopoly:shooters', (data) => setBoard(data.board));
    socket.on('monopoly:reset', () => setBoard(null));

    // Battleship updates
    socket.on('battleship:game-start', () => fetchBattleshipState());
    socket.on('battleship:hit', () => fetchBattleshipState());
    socket.on('battleship:miss', () => fetchBattleshipState());
    socket.on('battleship:sunk', () => fetchBattleshipState());
    socket.on('battleship:eliminated', () => fetchBattleshipState());
    socket.on('battleship:turn', () => fetchBattleshipState());
    socket.on('battleship:game-over', () => fetchBattleshipState());

    const pollInterval = setInterval(() => {
      fetchState();
      fetchBattleshipState();
    }, 5000);

    return () => {
      socket.disconnect();
      clearInterval(pollInterval);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [fetchState, fetchBattleshipState]);

  const startCountdown = (endsAt) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((endsAt - Date.now()) / 1000));
      setTimeLeft(remaining);
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }, 100);
  };

  // Admin actions
  const adminAction = async (endpoint, body = {}) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/monopoly/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-admin-token': getAdminToken() },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.board) setBoard(data.board);
    } catch (err) {
      console.error(`Monopoly action ${endpoint} failed:`, err);
    }
    setLoading(false);
  };

  const handleCreate = () => adminAction('create');
  const handleAdvance = () => adminAction('advance');
  const handleStartVoting = () => adminAction('start-voting', { duration: voteDuration * 1000 });
  const handleEndVoting = () => adminAction('end-voting');
  const handleReveal = () => adminAction('reveal');
  const handleSelectShooters = () => adminAction('select-shooters', { maxPerTeam: maxShooters });
  const handleReset = () => { if (window.confirm('Reset the Komok-opoly board?')) adminAction('reset'); };

  if (!board) {
    return (
      <div className="bm-container">
        <div className="bm-empty">
          <h2>Komok-opoly</h2>
          <p>No board created yet.</p>
          {isAdmin && (
            <button className="bm-btn bm-btn-primary" onClick={handleCreate}>
              Create Board
            </button>
          )}
        </div>
      </div>
    );
  }

  const currentSquare = board.currentPosition >= 0 ? board.squares[board.currentPosition] : null;

  // Build map of which teams are on which square
  const teamsOnSquare = {};
  if (board.teamPositions) {
    for (const [team, pos] of Object.entries(board.teamPositions)) {
      if (!teamsOnSquare[pos]) teamsOnSquare[pos] = [];
      teamsOnSquare[pos].push(team);
    }
  }

  return (
    <div className={`bm-container ${compact ? 'bm-compact' : ''}`}>
      {!compact && (
        <button
          className="bm-fullscreen-btn"
          onClick={() => document.documentElement.requestFullscreen?.()}
          title="Fullscreen"
        >&#x26F6;</button>
      )}

      {/* Main board */}
      <div className="bm-board-area">
        <div className="bm-grid">
          {/* Render 16 squares */}
          {board.squares.map((square, i) => (
            <div
              key={i}
              className={`bm-square ${square.state} ${i === board.currentPosition ? 'bm-current' : ''}`}
              style={{
                gridColumn: square.position.col,
                gridRow: square.position.row,
                '--sq-color': square.color,
              }}
              onClick={isAdmin && square.state === 'locked' && board.phase !== 'voting' ? handleAdvance : undefined}
            >
              <div className="bm-sq-icon">{square.icon}</div>
              <div className="bm-sq-name">{square.name}</div>
              {square.state === 'completed' && square.answer && (
                <div className="bm-sq-answer">{square.answer}</div>
              )}
              {/* Komok question marker */}
              {i === board.currentPosition && (
                <div className="bm-piece bm-piece-komok">
                  <img src="/komok_logo.webp" alt="" />
                </div>
              )}
              {/* Team pieces */}
              {teamsOnSquare[i] && (
                <div className="bm-team-pieces">
                  {teamsOnSquare[i].map(team => (
                    <div
                      key={team}
                      className="bm-team-piece"
                      style={{ '--team-color': TEAM_INFO[team].color }}
                      title={TEAM_INFO[team].name}
                    >
                      {TEAM_INFO[team].piece}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Center area */}
          <div className="bm-center">
            {board.phase === 'idle' && !currentSquare && (
              <div className="bm-center-idle">
                <img src="/komok_logo.webp" alt="KOMOK" className="bm-center-logo" />
                <h2>KOMOK-OPOLY</h2>

                {/* Standalone race standings */}
                {board.teamStats && board.roundNumber > 0 && (
                  <div className="bm-standings">
                    <div className="bm-standings-title">Race Standings — Round {board.roundNumber}</div>
                    {[...TEAM_ORDER]
                      .sort((a, b) => {
                        const posA = board.teamPositions?.[a] || 0;
                        const posB = board.teamPositions?.[b] || 0;
                        if (posB !== posA) return posB - posA;
                        return (board.teamStats[b]?.totalCorrect || 0) - (board.teamStats[a]?.totalCorrect || 0);
                      })
                      .map((team, rank) => {
                        const stats = board.teamStats[team] || { totalCorrect: 0, totalVotes: 0 };
                        const pos = board.teamPositions?.[team] || 0;
                        return (
                          <div key={team} className="bm-standing-row" style={{ borderColor: TEAM_INFO[team].color }}>
                            <span className="bm-standing-rank">#{rank + 1}</span>
                            <span className="bm-standing-piece">{TEAM_INFO[team].piece}</span>
                            <span className="bm-standing-name" style={{ color: TEAM_INFO[team].color }}>{TEAM_INFO[team].name}</span>
                            <span className="bm-standing-stats">{stats.totalCorrect} correct</span>
                            <span className="bm-standing-pos">Sq {pos}</span>
                          </div>
                        );
                      })}
                  </div>
                )}

                {/* Battleship fleet status */}
                {battleshipState && (
                  <div className="bm-bs-status">
                    {TEAM_ORDER.map(team => {
                      const t = battleshipState.teams?.[team];
                      if (!t) return null;
                      return (
                        <div key={team} className={`bm-bs-team ${t.isEliminated ? 'eliminated' : ''}`} style={{ borderColor: TEAM_INFO[team].color }}>
                          <span className="bm-bs-team-name" style={{ color: TEAM_INFO[team].color }}>{TEAM_INFO[team].name}</span>
                          <span className="bm-bs-ships">{t.shipsRemaining}/5 ships</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* First-time idle, no rounds yet */}
                {(!board.teamStats || board.roundNumber === 0) && !battleshipState && (
                  <div className="bm-waiting-start">Waiting for first round...</div>
                )}
              </div>
            )}

            {currentSquare && (
              <div className="bm-center-game">
                <div className="bm-center-title">KOMOK-OPOLY</div>
                <div className="bm-game-icon">{currentSquare.icon}</div>
                <div className="bm-game-name">{currentSquare.name}</div>
                <div className="bm-game-prompt">{currentSquare.prompt}</div>

                <div className="bm-game-options">
                  {currentSquare.options.map((opt, oi) => (
                    <div
                      key={opt}
                      className={`bm-game-option ${board.answer === opt ? 'bm-correct' : ''} ${board.answer && board.answer !== opt ? 'bm-wrong' : ''}`}
                      style={{ '--opt-color': currentSquare.optionColors?.[oi] || '#666' }}
                    >
                      {opt}
                    </div>
                  ))}
                </div>

                {board.phase === 'voting' && (
                  <div className="bm-voting-status">
                    <div className="bm-timer-ring">
                      <span className="bm-timer-num">{timeLeft != null ? timeLeft : '...'}</span>
                    </div>
                    <div className="bm-vote-count">{board.voteCount} votes</div>
                  </div>
                )}

                {board.phase === 'voted' && (
                  <div className="bm-voted-status">
                    <div className="bm-vote-count">{board.voteCount} votes cast</div>
                    <div className="bm-waiting-reveal">Waiting for reveal...</div>
                  </div>
                )}

                {board.answer && (
                  <div className="bm-answer-display">
                    <div className="bm-answer-label">Answer</div>
                    <div className="bm-answer-value">{board.answer}</div>
                    {board.detail && (
                      <div className="bm-answer-detail">
                        {board.detail.value != null && `Value: ${board.detail.value}`}
                        {board.detail.d1 != null && `Dice: ${board.detail.d1} + ${board.detail.d2} = ${board.detail.total}`}
                        {board.detail.card && `Card: ${board.detail.card}`}
                        {board.detail.first != null && `${board.detail.first} → ${board.detail.second}`}
                        {board.detail.housePick && `House played: ${board.detail.housePick}`}
                        {board.detail.handTotal != null && `Hand: ${board.detail.handTotal} + ${board.detail.nextCard} = ${board.detail.newTotal}`}
                      </div>
                    )}
                  </div>
                )}

                {board.teamResults && (
                  <div className="bm-team-results">
                    {board.turnOrder.map((team, rank) => {
                      const r = board.teamResults[team];
                      const advance = [4, 3, 2, 1][rank] || 1;
                      return (
                        <div key={team} className="bm-team-result" style={{ borderColor: TEAM_INFO[team].color }}>
                          <span className="bm-rank">#{rank + 1}</span>
                          <span className="bm-team-piece-icon">{TEAM_INFO[team].piece}</span>
                          <span className="bm-team-label" style={{ color: TEAM_INFO[team].color }}>{TEAM_INFO[team].name}</span>
                          <span className="bm-team-score">{r.correct} correct</span>
                          <span className="bm-team-advance">+{advance}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {board.shooters && (
                  <div className="bm-shooters-display">
                    <div className="bm-shooters-title">SHOOTERS</div>
                    {TEAM_ORDER.map(team => {
                      const s = board.shooters[team];
                      if (!s || s.length === 0) return (
                        <div key={team} className="bm-shooter-team" style={{ borderColor: TEAM_INFO[team].color }}>
                          <span style={{ color: TEAM_INFO[team].color }}>{TEAM_INFO[team].name}</span>
                          <span className="bm-no-shooters">No shooters</span>
                        </div>
                      );
                      return (
                        <div key={team} className="bm-shooter-team" style={{ borderColor: TEAM_INFO[team].color }}>
                          <span style={{ color: TEAM_INFO[team].color }}>{TEAM_INFO[team].name}</span>
                          <span className="bm-shooter-count">{s.length} shooter{s.length !== 1 ? 's' : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Admin controls */}
      {isAdmin && (
        <div className="bm-controls">
          <h3>Game Board Controls</h3>

          <div className="bm-phase-badge">{board.phase.toUpperCase()}</div>

          <div className="bm-control-group">
            <button
              className="bm-btn bm-btn-primary"
              onClick={handleAdvance}
              disabled={loading || board.phase === 'voting'}
            >
              Advance to Next Square
            </button>
          </div>

          {board.phase === 'idle' && currentSquare && (
            <div className="bm-control-group">
              <label>
                Vote duration (seconds):
                <input
                  type="number"
                  value={voteDuration}
                  onChange={e => setVoteDuration(parseInt(e.target.value) || 10)}
                  min={5}
                  max={60}
                  className="bm-input"
                />
              </label>
              <button className="bm-btn bm-btn-green" onClick={handleStartVoting} disabled={loading}>
                Start Voting
              </button>
            </div>
          )}

          {board.phase === 'voting' && (
            <div className="bm-control-group">
              <div className="bm-live-timer">
                {timeLeft != null ? `${timeLeft}s remaining` : 'Voting...'}
              </div>
              <div className="bm-live-votes">{board.voteCount} votes</div>
              <button className="bm-btn bm-btn-orange" onClick={handleEndVoting} disabled={loading}>
                End Voting Now
              </button>
            </div>
          )}

          {board.phase === 'voted' && (
            <div className="bm-control-group">
              <button className="bm-btn bm-btn-blue" onClick={handleReveal} disabled={loading}>
                Reveal Answer
              </button>
            </div>
          )}

          {board.phase === 'reveal' && (
            <div className="bm-control-group">
              <label>
                Max shooters per team:
                <input
                  type="number"
                  value={maxShooters}
                  onChange={e => setMaxShooters(parseInt(e.target.value) || 4)}
                  min={1}
                  max={10}
                  className="bm-input"
                />
              </label>
              <button className="bm-btn bm-btn-green" onClick={handleSelectShooters} disabled={loading}>
                Select Shooters
              </button>
            </div>
          )}

          {board.phase === 'results' && (
            <div className="bm-control-group">
              <div className="bm-results-summary">
                <strong>Turn Order:</strong>
                <ol className="bm-turn-order">
                  {board.turnOrder.map(team => (
                    <li key={team} style={{ color: TEAM_INFO[team].color }}>
                      {TEAM_INFO[team].name}
                      {board.shooters?.[team] ? ` (${board.shooters[team].length} shooters)` : ' (0 shooters)'}
                    </li>
                  ))}
                </ol>
              </div>
              <button className="bm-btn bm-btn-primary" onClick={handleAdvance} disabled={loading}>
                Next Round
              </button>
            </div>
          )}

          <hr className="bm-divider" />

          <div className="bm-control-group">
            <button className="bm-btn bm-btn-red" onClick={handleReset} disabled={loading}>
              Reset Board
            </button>
            <button className="bm-btn" onClick={handleCreate} disabled={loading}>
              New Board
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BattleshipMonopolyBoard;
